import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, DocumentArrowUpIcon, CheckCircleIcon, ExclamationCircleIcon, DocumentIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { uploadArticlePdf, processArticlePdf, getPdfStatus } from '../../Requests/ArticleBibliographyRequests'
import { getTeamUuid } from '../../utils/cookies'
import ErrorPopup from '../PopupNotifications/ErrorPopup'

const POLL_INTERVAL_MS = 3000
const MAX_POLLS = 60 // 3 minutes max

export default function PdfImportModal({ open, setOpen, projectUuid, onArticleCreated }) {
    const { t } = useTranslation()
    // phases: drop | uploading | uploaded | processing | done | error
    const [phase, setPhase] = useState('drop')
    const [dragActive, setDragActive] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [article, setArticle] = useState(null)
    const [fileName, setFileName] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const [isStartingProcess, setIsStartingProcess] = useState(false)
    const fileInputRef = useRef(null)
    const pollRef = useRef(null)
    const pollCountRef = useRef(0)

    const stopPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current)
            pollRef.current = null
        }
    }

    useEffect(() => {
        if (!open) {
            stopPolling()
            setPhase('drop')
            setDragActive(false)
            setUploadProgress(0)
            setArticle(null)
            setFileName('')
            setIsStartingProcess(false)
            pollCountRef.current = 0
        }
    }, [open])

    useEffect(() => () => stopPolling(), [])

    const startPolling = (articleId) => {
        pollCountRef.current = 0
        pollRef.current = setInterval(async () => {
            pollCountRef.current += 1
            if (pollCountRef.current > MAX_POLLS) {
                stopPolling()
                setPhase('error')
                setErrorMessage(t('articleBibliography.pdfImport.errors.timeout'))
                return
            }
            try {
                const res = await getPdfStatus(articleId)
                const status = res.data.status
                if (status === 'processed') {
                    stopPolling()
                    setArticle(res.data.article)
                    setPhase('done')
                } else if (status === 'failed') {
                    stopPolling()
                    setPhase('error')
                    setErrorMessage(t('articleBibliography.pdfImport.errors.processingFailed'))
                }
            } catch {
                // keep polling
            }
        }, POLL_INTERVAL_MS)
    }

    const handleFile = useCallback(async (file) => {
        if (!file) return
        if (file.type !== 'application/pdf') {
            setErrorMessage(t('articleBibliography.pdfImport.errors.pdfOnly'))
            return
        }
        if (file.size > 50 * 1024 * 1024) {
            setErrorMessage(t('articleBibliography.pdfImport.errors.tooLarge'))
            return
        }

        const formData = new FormData()
        formData.append('file', file)
        formData.append('team_uuid', getTeamUuid())
        formData.append('project_uuid', projectUuid)

        setFileName(file.name)
        setPhase('uploading')
        setUploadProgress(0)

        try {
            const res = await uploadArticlePdf(formData, (progress) => {
                setUploadProgress(Math.round((progress.loaded * 100) / progress.total))
            })
            setArticle(res.data)
            setPhase('uploaded')
        } catch (err) {
            setPhase('error')
            setErrorMessage(
                err.response?.status === 422
                    ? t('articleBibliography.pdfImport.errors.invalidFile')
                    : t('articleBibliography.pdfImport.errors.uploadFailed')
            )
        }
    }, [projectUuid, t])

    const handleExtractMetadata = async () => {
        if (!article || isStartingProcess) return
        setIsStartingProcess(true)
        try {
            await processArticlePdf(article.id)
            setPhase('processing')
            startPolling(article.id)
        } catch {
            setPhase('error')
            setErrorMessage(t('articleBibliography.pdfImport.errors.uploadFailed'))
        } finally {
            setIsStartingProcess(false)
        }
    }

    const handleSkip = () => {
        setOpen(false)
        if (onArticleCreated) onArticleCreated()
    }

    const onDragOver = (e) => { e.preventDefault(); setDragActive(true) }
    const onDragLeave = () => setDragActive(false)
    const onDrop = (e) => {
        e.preventDefault()
        setDragActive(false)
        const file = e.dataTransfer.files?.[0]
        if (file) handleFile(file)
    }
    const onFileInputChange = (e) => {
        const file = e.target.files?.[0]
        if (file) handleFile(file)
        e.target.value = ''
    }

    const handleConfirm = () => {
        setOpen(false)
        if (onArticleCreated) onArticleCreated()
    }

    const handleClose = () => {
        if (phase === 'uploading' || phase === 'processing') return
        setOpen(false)
    }

    const canClose = phase !== 'uploading' && phase !== 'processing'

    return (
        <>
            <Transition.Root show={open} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={handleClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
                        leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                                    {canClose && (
                                        <div className="absolute right-0 top-0 pr-4 pt-4">
                                            <button onClick={handleClose} className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none">
                                                <XMarkIcon className="h-6 w-6" />
                                            </button>
                                        </div>
                                    )}

                                    <div>
                                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 mb-1">
                                            {t('articleBibliography.pdfImport.title')}
                                        </Dialog.Title>
                                        <p className="text-sm text-gray-500 mb-6">
                                            {t('articleBibliography.pdfImport.subtitle')}
                                        </p>

                                        {/* Drop zone */}
                                        {phase === 'drop' && (
                                            <div
                                                onDragOver={onDragOver}
                                                onDragLeave={onDragLeave}
                                                onDrop={onDrop}
                                                onClick={() => fileInputRef.current?.click()}
                                                className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-10 cursor-pointer transition-colors ${
                                                    dragActive ? 'border-black bg-gray-50' : 'border-gray-300 hover:border-gray-400'
                                                }`}
                                            >
                                                <DocumentArrowUpIcon className="h-12 w-12 text-gray-400 mb-3" />
                                                <p className="text-sm font-medium text-gray-700">
                                                    {t('articleBibliography.pdfImport.dropzone.prompt')}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {t('articleBibliography.pdfImport.dropzone.hint')}
                                                </p>
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept="application/pdf"
                                                    className="hidden"
                                                    onChange={onFileInputChange}
                                                />
                                            </div>
                                        )}

                                        {/* Uploading */}
                                        {phase === 'uploading' && (
                                            <div className="flex flex-col items-center py-8 gap-4">
                                                <DocumentArrowUpIcon className="h-10 w-10 text-gray-400 animate-bounce" />
                                                <p className="text-sm font-medium text-gray-700">{t('articleBibliography.pdfImport.uploading')}</p>
                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className="bg-black h-2 rounded-full transition-all"
                                                        style={{ width: `${uploadProgress}%` }}
                                                    />
                                                </div>
                                                <p className="text-xs text-gray-500">{uploadProgress}%</p>
                                            </div>
                                        )}

                                        {/* Uploaded — ready to process */}
                                        {phase === 'uploaded' && (
                                            <div className="flex flex-col items-center py-8 gap-4">
                                                <div className="flex items-center justify-center w-14 h-14 rounded-full bg-green-50">
                                                    <DocumentIcon className="h-7 w-7 text-green-600" />
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-sm font-semibold text-gray-900">{t('articleBibliography.pdfImport.uploaded')}</p>
                                                    {fileName && (
                                                        <p className="text-xs text-gray-500 mt-1 max-w-xs truncate">{fileName}</p>
                                                    )}
                                                </div>
                                                <p className="text-xs text-gray-500 text-center max-w-sm">
                                                    {t('articleBibliography.pdfImport.uploadedHint')}
                                                </p>
                                            </div>
                                        )}

                                        {/* Processing */}
                                        {phase === 'processing' && (
                                            <div className="flex flex-col items-center py-10 gap-4">
                                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-black" />
                                                <p className="text-sm font-medium text-gray-700">{t('articleBibliography.pdfImport.processing')}</p>
                                                <p className="text-xs text-gray-500 text-center max-w-xs">
                                                    {t('articleBibliography.pdfImport.processingHint')}
                                                </p>
                                            </div>
                                        )}

                                        {/* Done */}
                                        {phase === 'done' && article && (
                                            <div className="space-y-4">
                                                <div className="flex items-center gap-2 text-green-700">
                                                    <CheckCircleIcon className="h-5 w-5" />
                                                    <span className="text-sm font-medium">{t('articleBibliography.pdfImport.done')}</span>
                                                </div>
                                                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                                                    <div>
                                                        <span className="font-medium text-gray-700">{t('articleBibliography.pdfImport.extracted.title')}: </span>
                                                        <span className="text-gray-900">{article.title}</span>
                                                    </div>
                                                    {article.authors?.length > 0 && (
                                                        <div>
                                                            <span className="font-medium text-gray-700">{t('articleBibliography.pdfImport.extracted.authors')}: </span>
                                                            <span className="text-gray-900">{article.authors.map(a => a.name).join(', ')}</span>
                                                        </div>
                                                    )}
                                                    {article.journal_name && (
                                                        <div>
                                                            <span className="font-medium text-gray-700">{t('articleBibliography.pdfImport.extracted.journal')}: </span>
                                                            <span className="text-gray-900">{article.journal_name}</span>
                                                        </div>
                                                    )}
                                                    {article.publication_year && (
                                                        <div>
                                                            <span className="font-medium text-gray-700">{t('articleBibliography.pdfImport.extracted.year')}: </span>
                                                            <span className="text-gray-900">{article.publication_year}</span>
                                                        </div>
                                                    )}
                                                    {article.doi && (
                                                        <div>
                                                            <span className="font-medium text-gray-700">DOI: </span>
                                                            <span className="text-gray-900">{article.doi}</span>
                                                        </div>
                                                    )}
                                                    {article.abstract && (
                                                        <div>
                                                            <span className="font-medium text-gray-700">{t('articleBibliography.pdfImport.extracted.abstract')}: </span>
                                                            <span className="text-gray-500 line-clamp-3">{article.abstract}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <p className="text-xs text-gray-500">{t('articleBibliography.pdfImport.editHint')}</p>
                                            </div>
                                        )}

                                        {/* Error */}
                                        {phase === 'error' && (
                                            <div className="flex flex-col items-center py-8 gap-3">
                                                <ExclamationCircleIcon className="h-10 w-10 text-red-500" />
                                                <p className="text-sm font-medium text-gray-700">{t('articleBibliography.pdfImport.errors.title')}</p>
                                                <p className="text-xs text-gray-500 text-center">{errorMessage}</p>
                                                <button
                                                    onClick={() => { setPhase('drop'); setErrorMessage('') }}
                                                    className="mt-2 text-sm text-black underline"
                                                >
                                                    {t('articleBibliography.pdfImport.tryAgain')}
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Footer */}
                                    <div className="mt-6 flex justify-end gap-3">
                                        {phase === 'uploaded' && (
                                            <>
                                                <button
                                                    onClick={handleSkip}
                                                    className="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                                >
                                                    {t('articleBibliography.pdfImport.skipMetadata')}
                                                </button>
                                                <button
                                                    onClick={handleExtractMetadata}
                                                    disabled={isStartingProcess}
                                                    className="inline-flex items-center rounded-md bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 disabled:opacity-60"
                                                >
                                                    {t('articleBibliography.pdfImport.extractMetadata')}
                                                </button>
                                            </>
                                        )}
                                        {phase === 'done' && (
                                            <button
                                                onClick={handleConfirm}
                                                className="inline-flex items-center rounded-md bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
                                            >
                                                {t('articleBibliography.pdfImport.confirm')}
                                            </button>
                                        )}
                                        {(phase === 'drop' || phase === 'error') && (
                                            <button
                                                onClick={handleClose}
                                                className="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                            >
                                                {t('common.cancel')}
                                            </button>
                                        )}
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            {errorMessage && phase !== 'error' && (
                <ErrorPopup text={errorMessage} handleClose={() => setErrorMessage('')} />
            )}
        </>
    )
}
